import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { Pill, PRIMARY_PILL_SX } from '../../Pill.js';
import type { ResultSelection } from '../../workflows.types.js';
import { Chips, Copy, GRID_SX, selectionSummary } from '../primitives.js';
import { SourceBriefStrip, VersionSummaryCard } from '../storyboard/StoryboardView.js';
import type { BannerStoryboardResult } from './bannerResult.js';

export function BannerStoryboardView({
  result,
  selection,
}: {
  result: BannerStoryboardResult;
  selection?: ResultSelection;
}): ReactNode {
  const { storyboard } = result;
  return (
    <Stack sx={{ gap: '24px', minWidth: 0 }}>
      <SourceBriefStrip brief={storyboard.source_brief} />
      {storyboard.warnings.map((warning) => (
        <Alert key={warning} severity="warning">
          {warning}
        </Alert>
      ))}
      <Box sx={{ ...GRID_SX, gap: '16px', alignItems: 'start' }}>
        {result.versions.map((version, index) => (
          <VersionSummaryCard
            key={`${version.name}-${version.strategy}`}
            version={version}
            index={index}
            selection={selection}
          >
            <Stack sx={{ gap: '8px' }}>
              <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
                {version.banner.headline}
              </Typography>
              {version.banner.subline === '' ? null : (
                <Typography sx={{ fontSize: 13 }}>{version.banner.subline}</Typography>
              )}
              <Pill sx={{ ...PRIMARY_PILL_SX, alignSelf: 'flex-start' }}>{version.banner.cta}</Pill>
            </Stack>
            <Copy label="Visual">{version.banner.visual}</Copy>
            <Copy label="Layout">{version.banner.layout}</Copy>
            <Copy label="Creative premise">{version.creative_premise}</Copy>
            <Chips
              values={version.banner.element_ids.map((id) => storyboard.elements[id]?.tag ?? id)}
            />
          </VersionSummaryCard>
        ))}
      </Box>
      {selection === undefined ? null : (
        <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
          {selectionSummary(selection.selectedIds.length, result.versions.length)}
        </Typography>
      )}
    </Stack>
  );
}
